// -----------------------------------------------------------------------------
// This file contains custom hooks to select slices of the Zustand store.
// It helps to avoid re-renders by selecting only the needed parts of the state,
// and using shallow comparison where appropriate.
//
// Each hook is focused on a specific part of the state, such as ProjectionState
// -----------------------------------------------------------------------------

// Zustand imports
import usePersistentStore from './persistent/persistentStore';
import { useShallow } from 'zustand/react/shallow';
import { ProjectionLayerState, ElementLayerState, SimulationItemsLayerState, SegmentsLayerState, DataLayerState } from './layerState';

// ----------- Projection ------------------------------------------------------------

type ProjectionSlice = {
    projection: ProjectionLayerState | undefined;
};

export function useProjectionSlice(): ProjectionSlice {
    return usePersistentStore(
        useShallow((s) => {
            const L = s.layers.layerStates[s.layers.activeLayerID];
            const projection = L?.projection;
            return { projection };
        })
    );
}

// ----------- Element ------------------------------------------------------------

type ElementSlice = {
    element: ElementLayerState | undefined;
};

export function useElementSlice(): ElementSlice {
    return usePersistentStore(
        useShallow((s) => {
            const L = s.layers.layerStates[s.layers.activeLayerID];
            const element = L?.element;
            return { element };
        })
    );
}

// ----------- Segments ------------------------------------------------------------

type SegmentsSlice = {
    segments: SegmentsLayerState | undefined;
};

export function useSegmentsSlice(): SegmentsSlice {
    return usePersistentStore(
        useShallow((s) => {
            const L = s.layers.layerStates[s.layers.activeLayerID];
            const segments = L?.segments;
            return { segments };
        })
    );
}

// ----------- SimulationItems ------------------------------------------------------------
type SimulationItemsSlice = {
    simulationItems: SimulationItemsLayerState | undefined;
};

export function useSimulationItemsSlice(): SimulationItemsSlice {
    return usePersistentStore(
        useShallow((s) => {
            const L = s.layers.layerStates[s.layers.activeLayerID];
            const simulationItems = L?.simulationItems;
            return { simulationItems };
        })
    );
}

// ----------- Data ------------------------------------------------------------
type DataLayerSlice = {
    data: DataLayerState | undefined;
};

export function useDataLayerSlice(): DataLayerSlice {
    return usePersistentStore(
        useShallow((s) => {
            const L = s.layers.layerStates[s.layers.activeLayerID];
            const data = L?.data;
            return { data };
        })
    );
}
